/**
 * Componente de métricas de inventario
 * Muestra el resumen del inventario (total productos, valor total, ventas, etc.)
 * Inventory metrics component - Displays inventory summary (total products, total value, sales, etc.)
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar, faTriangleExclamation, faBan, faCircleDollar } from '@fortawesome/pro-light-svg-icons';
import { SectionBox } from '@shared/components/ui/SectionBox';
import { formatCurrencyAmount, useCurrency } from '@modules/Orders/shared/hooks/useCurrency';
import { InventorySummary, InventoryProduct } from '../../models/InventoryApiResponse';

interface InventoryMetricsProps {
  summary?: InventorySummary;
  products?: InventoryProduct[];
  isLoading?: boolean;
}

export const InventoryMetrics = ({ summary, products = [] }: InventoryMetricsProps) => {
  const { t } = useTranslation('inventoryModule');
  // 🎯 Obtener código de moneda desde /me - Get currency code from /me
  const currency = useCurrency();

  // Calcular promedio de días de stock
  // Calculate average days of stock
  const averageDaysOfStock = useMemo(() => {
    if (!products || products.length === 0) return 0;
    const productsWithDays = products.filter(
      (p) => p.days_of_stock_remaining !== null && p.days_of_stock_remaining !== undefined,
    );
    if (productsWithDays.length === 0) return 0;
    const totalDays = productsWithDays.reduce((sum, p) => sum + (p.days_of_stock_remaining || 0), 0);
    return totalDays / productsWithDays.length;
  }, [products]);

  // Contar productos con menos de 30 días de stock
  // Count products with less than 30 days of stock
  const productsWithLessThan30Days = useMemo(() => {
    if (!products || products.length === 0) return 0;
    return products.filter(
      (p) =>
        p.days_of_stock_remaining !== null && p.days_of_stock_remaining !== undefined && p.days_of_stock_remaining < 30,
    ).length;
  }, [products]);

  // Métricas de inventario con sus iconos y colores correspondientes
  // Inventory metrics with their corresponding icons and colors
  const metrics = [
    {
      title: t('metrics.average_days_of_stock.title'),
      value: averageDaysOfStock.toFixed(1),
      icon: faCalendar,
      iconBgClass: 'bg-card-bg',
      iconTextClass: 'text-info',
      dataTestId: 'inventory-metric-average-days-stock',
    },
    {
      title: t('metrics.products_less_than_30_days.title'),
      value: productsWithLessThan30Days.toLocaleString(),
      icon: faTriangleExclamation,
      iconBgClass: 'bg-card-bg',
      iconTextClass: 'text-warning',
      dataTestId: 'inventory-metric-products-less-30-days',
    },
    {
      title: t('metrics.products_without_sales_30_days.title'),
      value: summary ? summary.products_without_sales.toLocaleString() : '0',
      icon: faBan,
      iconBgClass: 'bg-card-bg',
      iconTextClass: 'text-error',
      dataTestId: 'inventory-metric-products-without-sales',
    },
    {
      title: t('metrics.total_inventory_value.title'),
      value: formatCurrencyAmount(summary?.total_inventory_value ?? 0, currency),
      icon: faCircleDollar,
      iconBgClass: 'bg-card-bg',
      iconTextClass: 'text-success',
      dataTestId: 'inventory-metric-total-value',
    },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {metrics.map((metric, index) => (
        <SectionBox key={index} data-testid={metric.dataTestId} className="!space-y-0">
          {/* Contenedor principal de la métrica - Main metric container */}
          <div className="flex items-center gap-3 h-full">
            {/* Icono de la métrica - Metric icon */}
            <div className={`w-8 h-8 rounded-md ${metric.iconBgClass} flex items-center justify-center flex-shrink-0`}>
              <FontAwesomeIcon icon={metric.icon} className={`w-3.5 h-3.5 ${metric.iconTextClass}`} />
            </div>
            {/* Información de la métrica - Metric information */}
            <div className="flex-1">
              {/* Título de la métrica - Metric title */}
              <h3 className="text-sm font-medium text-complementary-text mb-1">{metric.title}</h3>
              {/* Valor de la métrica - Metric value */}
              <p className="text-base font-bold text-title">{metric.value}</p>
            </div>
          </div>
        </SectionBox>
      ))}
    </div>
  );
};
